import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { listCdnProfiles, uploadCdnObject } from '../CdnAssets/api';
import {
  formatSubscriberDateTime,
  parseSubscriberDate,
  subscriberDaysAgoLabel,
} from '../../lib/subscriberDates';
import {
  buildFieldUploadCdnKey,
  fieldUploadAccept,
  fieldUploadCdnKeyTemplate,
  fieldUploadEnabled,
  fieldUploadStorageHint,
  groupDefinitionsByAdminGroup,
  isLikelyImageUrl,
  isLinkedInUrlCustomField,
  linkedinCustomFieldHref,
  parseDraftForSave,
  optionDisplayLabel,
  sortDefinitions,
  validateFieldUploadFile,
  valueToDraft,
  type CustomDataMap,
  type SubscriberFieldDefinitionRow,
} from '../../lib/subscriberCustomFields';

const AUTOSAVE_DEBOUNCE_MS = 700;

function fieldSavesImmediately(fieldType: SubscriberFieldDefinitionRow['field_type']): boolean {
  return fieldType === 'boolean' || fieldType === 'select' || fieldType === 'multi_select';
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

const urlAppendButtonClass =
  'inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed';

const labelClass = 'text-sm font-medium text-gray-700 dark:text-gray-300';
const helpClass = 'text-xs text-gray-500 dark:text-gray-400 mt-2';
const fieldRowClass =
  'grid grid-cols-1 sm:grid-cols-[6.5rem_1fr] gap-x-4 gap-y-2 px-6 py-5 items-center sm:items-start';

function groupHasStoredOrDraftChanges(
  items: SubscriberFieldDefinitionRow[],
  customData: CustomDataMap | null | undefined,
  drafts: Record<string, string>,
): boolean {
  return items.some((def) => {
    const stored = valueToDraft(customData?.[def.key], def.field_type);
    const draft = drafts[def.key] ?? stored;
    return stored.trim() !== '' || draft !== stored;
  });
}

function FieldRow({
  def,
  draftVal,
  updatedAt,
  subscriberId,
  cdnProfileId,
  cdnProfilesLoading,
  cdnProfilesError,
  onChange,
  onBlur,
  onSaveImmediate,
}: {
  def: SubscriberFieldDefinitionRow;
  draftVal: string;
  updatedAt?: string | null;
  subscriberId: string;
  cdnProfileId: string | null;
  cdnProfilesLoading?: boolean;
  cdnProfilesError?: string | null;
  onChange: (v: string) => void;
  onBlur?: () => void;
  onSaveImmediate: (v: string) => void;
}) {
  const updatedDate = parseSubscriberDate(updatedAt);
  const updatedAgo = updatedDate ? subscriberDaysAgoLabel(updatedDate) : null;

  return (
    <div className={fieldRowClass}>
      <div>
        <label htmlFor={`scf-${def.id}`} className={labelClass}>
          {def.label}
          {def.ui.required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
        {updatedAgo ? (
          <p
            className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums"
            title={formatSubscriberDateTime(updatedAt)}
          >
            {updatedAgo}
          </p>
        ) : null}
      </div>
      <div className="min-w-0 w-full">
        <FieldInput
          def={def}
          value={draftVal}
          subscriberId={subscriberId}
          cdnProfileId={cdnProfileId}
          cdnProfilesLoading={cdnProfilesLoading}
          cdnProfilesError={cdnProfilesError}
          onChange={onChange}
          onBlur={onBlur}
          onSaveImmediate={onSaveImmediate}
        />
        {fieldUploadEnabled(def) ? (
          <p className={helpClass}>{fieldUploadStorageHint(def)}</p>
        ) : def.ui.help_text ? (
          <p className={helpClass}>{def.ui.help_text}</p>
        ) : null}
      </div>
    </div>
  );
}

export default function SubscriberCustomFieldsForm({
  subscriberId,
  customData,
  definitions,
  definitionsLoading,
  onFieldSaved,
  compact = false,
}: {
  subscriberId: string;
  customData: CustomDataMap | null | undefined;
  definitions: SubscriberFieldDefinitionRow[];
  definitionsLoading: boolean;
  /** Patch parent read cache after a single field persists (avoids full-page reload). */
  onFieldSaved?: (key: string, value: unknown | null) => void;
  /** When true, omit outer heading (parent supplies section title). */
  compact?: boolean;
}) {
  const visibleDefs = useMemo(
    () =>
      sortDefinitions(
        definitions.filter((d) => d.active && d.ui.show_on_detail !== false),
      ),
    [definitions],
  );

  const grouped = useMemo(() => groupDefinitionsByAdminGroup(visibleDefs), [visibleDefs]);

  const needsCdnUpload = useMemo(
    () => visibleDefs.some((d) => fieldUploadEnabled(d)),
    [visibleDefs],
  );

  const [cdnProfileId, setCdnProfileId] = useState<string | null>(null);
  const [cdnProfilesLoading, setCdnProfilesLoading] = useState(false);
  const [cdnProfilesError, setCdnProfilesError] = useState<string | null>(null);

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [fieldUpdatedAt, setFieldUpdatedAt] = useState<Record<string, string>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const storedRef = useRef<Record<string, string>>({});
  const saveTimersRef = useRef<Record<string, number>>({});
  const initialCustomDataRef = useRef(customData);

  useEffect(() => {
    initialCustomDataRef.current = customData;
  }, [subscriberId, customData]);

  useEffect(() => {
    if (!needsCdnUpload) {
      setCdnProfileId(null);
      setCdnProfilesLoading(false);
      setCdnProfilesError(null);
      return;
    }
    let cancelled = false;
    setCdnProfilesLoading(true);
    setCdnProfilesError(null);
    void listCdnProfiles()
      .then((profiles) => {
        if (cancelled) return;
        const id = profiles[0]?.id ?? null;
        setCdnProfileId(id);
        if (!id) {
          setCdnProfilesError('CDN profile missing — check R2 secrets and cdn-assets deploy.');
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setCdnProfileId(null);
        setCdnProfilesError(
          e instanceof Error ? e.message : 'Could not load CDN profiles',
        );
      })
      .finally(() => {
        if (!cancelled) setCdnProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsCdnUpload]);

  useEffect(() => {
    let cancelled = false;

    async function loadFieldTimestamps() {
      if (!subscriberId) {
        setFieldUpdatedAt({});
        return;
      }

      try {
        const { data, error } = await supabase
          .from('subscriber_field_values')
          .select('field_id, updated_at')
          .eq('subscriber_id', subscriberId);
        if (error) throw error;
        if (cancelled) return;

        const idToKey = new Map(definitions.map((d) => [d.id, d.key]));
        const next: Record<string, string> = {};
        for (const row of data ?? []) {
          const key = idToKey.get(String(row.field_id));
          if (key && typeof row.updated_at === 'string') {
            next[key] = row.updated_at;
          }
        }
        setFieldUpdatedAt(next);
      } catch {
        if (!cancelled) setFieldUpdatedAt({});
      }
    }

    void loadFieldTimestamps();
    return () => {
      cancelled = true;
    };
  }, [subscriberId, definitions]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const def of visibleDefs) {
      const val = customData?.[def.key];
      next[def.key] = valueToDraft(val, def.field_type);
    }
    setDrafts(next);
    storedRef.current = next;
    return () => {
      for (const id of Object.values(saveTimersRef.current)) {
        window.clearTimeout(id);
      }
      saveTimersRef.current = {};
    };
  }, [visibleDefs, subscriberId]);

  useEffect(() => {
    const initial = new Set<string>();
    const snapshot = initialCustomDataRef.current;
    for (const { group, items } of grouped) {
      if (grouped.length === 1 || groupHasStoredOrDraftChanges(items, snapshot, {})) {
        initial.add(group);
      }
    }
    if (initial.size === 0 && grouped.length > 0) {
      initial.add(grouped[0].group);
    }
    setExpandedGroups(initial);
  }, [grouped, subscriberId]);

  const saveSingleField = useCallback(
    async (def: SubscriberFieldDefinitionRow, raw: string) => {
      const stored = storedRef.current[def.key] ?? '';
      if (raw === stored) return;

      try {
        const parsed = parseDraftForSave(raw, def.field_type, def.options);
        if (!parsed.ok) {
          toast.error(`${def.label}: ${parsed.error}`);
          const revert = valueToDraft(customData?.[def.key], def.field_type);
          setDrafts((d) => ({ ...d, [def.key]: revert }));
          return;
        }

        const savedAt = new Date().toISOString();

        if (parsed.value === null) {
          const { error } = await supabase
            .from('subscriber_field_values')
            .delete()
            .eq('subscriber_id', subscriberId)
            .eq('field_id', def.id);
          if (error) throw error;
          setFieldUpdatedAt((prev) => {
            const next = { ...prev };
            delete next[def.key];
            return next;
          });
        } else {
          const { error } = await supabase.from('subscriber_field_values').upsert(
            {
              subscriber_id: subscriberId,
              field_id: def.id,
              value: parsed.value,
              updated_at: savedAt,
            },
            { onConflict: 'subscriber_id,field_id' },
          );
          if (error) throw error;
          setFieldUpdatedAt((prev) => ({ ...prev, [def.key]: savedAt }));
        }

        storedRef.current[def.key] = raw;
        onFieldSaved?.(def.key, parsed.value);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : `${def.label}: save failed`);
        const revert = valueToDraft(customData?.[def.key], def.field_type);
        setDrafts((d) => ({ ...d, [def.key]: revert }));
        storedRef.current[def.key] = revert;
      }
    },
    [customData, onFieldSaved, subscriberId],
  );

  const scheduleFieldSave = useCallback(
    (def: SubscriberFieldDefinitionRow, raw: string, immediate = false) => {
      if (saveTimersRef.current[def.key] != null) {
        window.clearTimeout(saveTimersRef.current[def.key]);
        delete saveTimersRef.current[def.key];
      }

      const stored = storedRef.current[def.key] ?? '';
      if (raw === stored) return;

      if (immediate || fieldSavesImmediately(def.field_type)) {
        void saveSingleField(def, raw);
        return;
      }

      saveTimersRef.current[def.key] = window.setTimeout(() => {
        delete saveTimersRef.current[def.key];
        void saveSingleField(def, raw);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [saveSingleField],
  );

  const handleFieldChange = useCallback(
    (def: SubscriberFieldDefinitionRow, value: string) => {
      setDrafts((d) => ({ ...d, [def.key]: value }));
      scheduleFieldSave(def, value);
    },
    [scheduleFieldSave],
  );

  const handleFieldSaveImmediate = useCallback(
    (def: SubscriberFieldDefinitionRow, value: string) => {
      setDrafts((d) => ({ ...d, [def.key]: value }));
      scheduleFieldSave(def, value, true);
    },
    [scheduleFieldSave],
  );

  const handleFieldBlur = useCallback(
    (def: SubscriberFieldDefinitionRow) => {
      if (fieldSavesImmediately(def.field_type)) return;
      const raw = drafts[def.key] ?? '';
      if (saveTimersRef.current[def.key] != null) {
        window.clearTimeout(saveTimersRef.current[def.key]);
        delete saveTimersRef.current[def.key];
      }
      void saveSingleField(def, raw);
    },
    [drafts, saveSingleField],
  );

  const allExpanded =
    grouped.length > 0 && grouped.every((g) => expandedGroups.has(g.group));
  const allCollapsed = grouped.every((g) => !expandedGroups.has(g.group));

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  if (definitionsLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 px-6 py-8">Loading…</p>;
  }

  if (visibleDefs.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 px-6 py-8">
        No custom fields.{' '}
        <a href="/subscriber-field-definitions" className="text-emerald-600 dark:text-emerald-400 hover:underline">
          Add fields
        </a>
      </p>
    );
  }

  const renderFields = (items: SubscriberFieldDefinitionRow[]) => (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {items.map((def) => (
        <FieldRow
          key={def.id}
          def={def}
          draftVal={drafts[def.key] ?? ''}
          updatedAt={fieldUpdatedAt[def.key]}
          subscriberId={subscriberId}
          cdnProfileId={cdnProfileId}
          cdnProfilesLoading={cdnProfilesLoading}
          cdnProfilesError={cdnProfilesError}
          onChange={(v) => handleFieldChange(def, v)}
          onBlur={() => handleFieldBlur(def)}
          onSaveImmediate={(v) => handleFieldSaveImmediate(def, v)}
        />
      ))}
    </div>
  );

  return (
    <FormShell compact={compact}>
      {!compact ? (
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
          Custom fields
        </h2>
      ) : null}
      {!compact && cdnProfilesError ? (
        <p className="text-sm text-red-700 dark:text-red-300">{cdnProfilesError}</p>
      ) : null}

      {grouped.length > 1 ? (
        <div className="flex items-center justify-end gap-2 px-6 pt-2 pb-1">
          <button
            type="button"
            onClick={() => setExpandedGroups(new Set(grouped.map((g) => g.group)))}
            disabled={allExpanded}
            className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setExpandedGroups(new Set())}
            disabled={allCollapsed}
            className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Collapse all
          </button>
        </div>
      ) : null}

      {grouped.length === 1 ? (
        renderFields(grouped[0].items)
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {grouped.map(({ group, items }) => {
            const open = expandedGroups.has(group);
            const filled = items.some((def) => {
              const stored = valueToDraft(customData?.[def.key], def.field_type);
              return stored.trim() !== '';
            });

            return (
              <section key={group}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="w-full flex items-center gap-2 px-6 py-3 text-left bg-gray-50/80 dark:bg-gray-900/30 hover:bg-gray-100 dark:hover:bg-gray-900/50 transition-colors"
                  aria-expanded={open}
                >
                  {open ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
                  )}
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{group}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {items.length} field{items.length === 1 ? '' : 's'}
                  </span>
                  {filled ? (
                    <span className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                      has data
                    </span>
                  ) : null}
                </button>
                {open ? renderFields(items) : null}
              </section>
            );
          })}
        </div>
      )}
    </FormShell>
  );
}

function FormShell({ compact, children }: { compact: boolean; children: ReactNode }) {
  return <div className={compact ? '' : 'space-y-4 px-6 py-4'}>{children}</div>;
}

function FieldInput({
  def,
  value,
  subscriberId,
  cdnProfileId,
  cdnProfilesLoading,
  cdnProfilesError,
  onChange,
  onBlur,
  onSaveImmediate,
}: {
  def: SubscriberFieldDefinitionRow;
  value: string;
  subscriberId: string;
  cdnProfileId: string | null;
  cdnProfilesLoading?: boolean;
  cdnProfilesError?: string | null;
  onChange: (v: string) => void;
  onBlur?: () => void;
  onSaveImmediate: (v: string) => void;
}) {
  const id = `scf-${def.id}`;

  if (def.field_type === 'url' && fieldUploadEnabled(def)) {
    return (
      <UrlFieldWithUpload
        id={id}
        def={def}
        value={value}
        subscriberId={subscriberId}
        cdnProfileId={cdnProfileId}
        cdnProfilesLoading={cdnProfilesLoading}
        cdnProfilesError={cdnProfilesError}
        onChange={onChange}
        onBlur={onBlur}
        onSaveImmediate={onSaveImmediate}
      />
    );
  }

  if (def.field_type === 'boolean') {
    return (
      <select
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      >
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (def.field_type === 'select' && def.options.length > 0) {
    return (
      <select
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      >
        <option value="">—</option>
        {def.options.map((opt) => (
          <option key={opt} value={opt}>
            {optionDisplayLabel(opt)}
          </option>
        ))}
      </select>
    );
  }

  if (def.field_type === 'multi_select' && def.options.length > 0) {
    const selected = new Set(
      value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
    return (
      <div className="space-y-2 rounded-lg border border-gray-300 dark:border-gray-600 p-3">
        {def.options.map((opt) => (
          <label
            key={opt}
            className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer"
          >
            <input
              type="checkbox"
              className="mt-1 rounded border-gray-300 dark:border-gray-600"
              checked={selected.has(opt)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                onChange([...next].join(', '));
              }}
            />
            <span>{optionDisplayLabel(opt)}</span>
          </label>
        ))}
      </div>
    );
  }

  if (def.field_type === 'textarea') {
    return (
      <textarea
        id={id}
        rows={3}
        className={inputClass}
        value={value}
        placeholder={def.ui.placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  if (isLinkedInUrlCustomField(def)) {
    const href = linkedinCustomFieldHref(value);
    return (
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          id={id}
          type={def.field_type === 'url' ? 'url' : 'text'}
          className={`${inputClass} min-w-0 flex-1`}
          value={value}
          placeholder={def.ui.placeholder ?? 'https://linkedin.com/in/…'}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
        <button
          type="button"
          disabled={!href}
          onClick={() => {
            if (href) window.open(href, '_blank', 'noopener,noreferrer');
          }}
          title={href ? 'Open LinkedIn profile in new tab' : 'Enter a LinkedIn URL first'}
          aria-label="Open LinkedIn profile in new tab"
          className={urlAppendButtonClass}
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
        </button>
      </div>
    );
  }

  const inputType =
    def.field_type === 'number'
      ? 'number'
      : def.field_type === 'date'
        ? 'date'
        : def.field_type === 'url'
          ? 'url'
          : 'text';

  return (
    <input
      id={id}
      type={inputType}
      className={inputClass}
      value={value}
      placeholder={def.ui.placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function UrlFieldWithUpload({
  id,
  def,
  value,
  subscriberId,
  cdnProfileId,
  cdnProfilesLoading,
  cdnProfilesError,
  onChange,
  onBlur,
  onSaveImmediate,
}: {
  id: string;
  def: SubscriberFieldDefinitionRow;
  value: string;
  subscriberId: string;
  cdnProfileId: string | null;
  cdnProfilesLoading?: boolean;
  cdnProfilesError?: string | null;
  onChange: (v: string) => void;
  onBlur?: () => void;
  onSaveImmediate: (v: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const accept = fieldUploadAccept(def.ui);
  const showPreview = value.trim() !== '' && isLikelyImageUrl(value);
  const uploadBlockedReason = uploading
    ? 'Uploading…'
    : cdnProfilesLoading
      ? 'Loading CDN…'
      : cdnProfilesError
        ? cdnProfilesError
        : !cdnProfileId
          ? 'CDN not ready'
          : 'Upload image';

  const handleUpload = async (file: File) => {
    const validationError = validateFieldUploadFile(file, def.ui);
    if (validationError) {
      toast.error(`${def.label}: ${validationError}`);
      return;
    }
    if (!cdnProfileId) {
      toast.error('CDN is not configured');
      return;
    }

    const key = buildFieldUploadCdnKey(
      fieldUploadCdnKeyTemplate(def.ui),
      subscriberId,
      file,
    );

    setUploading(true);
    try {
      const object = await uploadCdnObject({
        profileId: cdnProfileId,
        key,
        file,
        overwrite: true,
      });
      onSaveImmediate(object.publicUrl);
      toast.success(`${def.label} uploaded`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : `${def.label}: upload failed`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {showPreview ? (
        <div className="flex items-start gap-3">
          <img
            src={value.trim()}
            alt=""
            className="h-16 w-16 rounded-lg object-cover border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 shrink-0"
          />
          <button
            type="button"
            onClick={() => onSaveImmediate('')}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <X className="h-3 w-3" aria-hidden />
            Remove
          </button>
        </div>
      ) : null}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          id={id}
          type="url"
          className={`${inputClass} min-w-0 flex-1`}
          value={value}
          placeholder={def.ui.placeholder ?? 'https://…'}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
        <label
          title={uploadBlockedReason}
          aria-label={uploading ? 'Uploading…' : `Upload ${def.label}`}
          className={`inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0 cursor-pointer ${
            uploading || !cdnProfileId || cdnProfilesLoading ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Upload className="h-4 w-4" aria-hidden />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            className="sr-only"
            disabled={uploading || !cdnProfileId || cdnProfilesLoading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUpload(file);
            }}
          />
        </label>
      </div>
    </div>
  );
}
